import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';

export const useScreenshotProtection = () => {
  const [isBlurred, setIsBlurred] = useState(false);
  const { isAdmin } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Desativa a proteção na tela de login e rotas públicas de autenticação
    if (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/auth') {
      setIsBlurred(false);
      return;
    }

    // 📱 Em dispositivos móveis (Android/iOS), não usamos captura de tecla ou visibilitychange
    // para evitar tela preta ou travamento quando o sistema gerencia abas, notificações ou modo de economia
    const isMobile = typeof window !== 'undefined' && 
      (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
       ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 1));

    if (isMobile) {
      setIsBlurred(false);
      return;
    }

    let timer: NodeJS.Timeout | null = null;

    const triggerBlur = (duration = 3000) => {
      setIsBlurred(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setIsBlurred(false);
      }, duration);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // PrintScreen (Windows/Linux)
      const isPrintScreen = e.key === 'PrintScreen' || e.code === 'PrintScreen';
      
      // Windows Snipping Tool: Win + Shift + S
      const isWinSnipping = e.metaKey && e.shiftKey && (e.key === 's' || e.key === 'S');
      
      // Mac Screenshot: Cmd + Shift + 3 / 4 / 5
      const isMacScreenshot = e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key);

      // Bloquear também atalho de imprimir (Ctrl+P / Cmd+P)
      const isPrint = (e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P');

      if (isPrintScreen || isWinSnipping || isMacScreenshot || isPrint) {
        if (isAdmin) {
          const lastBlock = localStorage.getItem('last_admin_screenshot_block');
          const now = Date.now();
          if (!lastBlock || now - parseInt(lastBlock) > 1800000) {
            localStorage.setItem('last_admin_screenshot_block', now.toString());
            triggerBlur(3000);
          }
          return;
        }

        if (isPrint) e.preventDefault();
        
        triggerBlur(3000);
        
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText('');
          }
        } catch (err) {}
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const isPrintScreen = e.key === 'PrintScreen' || e.code === 'PrintScreen';
      if (isPrintScreen) {
        if (isAdmin) return;
        triggerBlur(3000);
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText('');
          }
        } catch (err) {}
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isAdmin, location.pathname]);

  return isBlurred;
};
